import {useEffect, useLayoutEffect, useState} from "react"
import {createPortal} from "react-dom"
import {useNullRef} from "../hooks/useNullRef"
import {useCallbackRef} from '../hooks/useCallbackRef'
import {useAbsolutePosition} from '../hooks/useAbsolutePosition'
import {useWindowSize} from '../hooks/useWindowSize'
import {useElementSize} from '../hooks/useElementSize'


type PopoverProps = {
    open: boolean;
    onClickOutside?: VoidFunction;
    onEscape?: VoidFunction;
    onClose?: VoidFunction;
    anchor: HTMLElement | null;
    children: React.ReactNode;
};

function Popover({children, open, anchor, onClickOutside, onClose, onEscape}: PopoverProps) {
    const [styles, setStyles] = useState<React.CSSProperties>({display: "none"})

    const popupRef = useNullRef<HTMLDivElement>()
    const onClickOutsideRef = useCallbackRef(onClickOutside)
    const onEscapeRef = useCallbackRef(onEscape)
    const onCloseRef = useCallbackRef(onClose)


    const [anchorRect, scrollRoot] = useAbsolutePosition(open ? anchor : null)
    const popupRect = useElementSize(popupRef.current)
    const windowSize = useWindowSize()
    // Technically `anchorFixed` should be more accurate, but it doesn't really matter when the scrollbar extends to the top of the window
    // const anchorFixed = useFixedPosition(open ?anchor : null)


    useLayoutEffect(() => {
        if(!anchorRect || !popupRect) return

        // FIXME: this still isn't right. Try on Schedule page.
        // const shouldPositionAbove = anchorRect.bottom + popupRect.height > windowSize.height;
        const shouldPositionAbove = false

        setStyles({
            zIndex: 100,  // Needs to be above VehicleTimeline. Should work with services/webpack/assets/scripts/components/Modal/Modal.less too
            position: "absolute",
            top: shouldPositionAbove ? anchorRect.top - popupRect.height : anchorRect.top + anchorRect.height,
            left: anchorRect.left,
        });
    }, [anchorRect, popupRect, popupRef, scrollRoot, windowSize.height])

    useEffect(() => {
        if (!open) return;

        const handleClickOutside = (ev: MouseEvent) => {
            if (popupRef.current && !popupRef.current.contains(ev.target as Node)) {
                (onClickOutsideRef.current ?? onCloseRef.current)?.();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [open, onClickOutsideRef, popupRef, onCloseRef]);

    useEffect(() => {
        if (!open) return;

        const handleKeyDown = (ev: KeyboardEvent) => {
            if (ev.key === "Escape") {
                ev.stopPropagation();
                (onEscapeRef.current ?? onCloseRef.current)?.();
            }
        };

        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [open, onEscapeRef, onCloseRef]);



    if(!open) return null
    return createPortal(
        <div ref={popupRef} style={styles}>
            {children}
        </div>,
        scrollRoot
    )
}

export default Popover
